import React, { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity, Animated } from 'react-native';
import { Text } from 'react-native-paper';
import { AVAQualisTheme } from '../theme/theme';

export default function AnnouncementBanner({ message, onClose }) {
  const [bounceAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(-100));
  const [pulseAnim] = useState(new Animated.Value(1));

  useEffect(() => {
    // Animação de entrada do banner
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 600,
      useNativeDriver: true,
    }).start();

    // Animação de bounce dos emojis
    const bounceAnimation = () => {
      Animated.sequence([
        Animated.timing(bounceAnim, {
          toValue: -6,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(bounceAnim, {
          toValue: 0,
          duration: 800,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setTimeout(bounceAnimation, 3000);
      });
    };

    // Animação de pulse sutil no texto principal
    const pulseAnimation = () => {
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.03,
          duration: 2000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setTimeout(pulseAnimation, 2000);
      });
    };

    bounceAnimation();
    pulseAnimation();
  }, [bounceAnim, slideAnim, pulseAnim]);

  const handleClose = () => {
    Animated.timing(slideAnim, {
      toValue: -100,
      duration: 400,
      useNativeDriver: true,
    }).start(() => {
      onClose();
    });
  };

  return (
    <Animated.View 
      style={[
        styles.banner, 
        { transform: [{ translateY: slideAnim }] }
      ]}
    >
      {/* Linha decorativa no topo */}
      <View style={styles.topLine} />
      
      {/* Decorações de fundo */}
      <View style={styles.backgroundDecoration} />
      <View style={styles.backgroundDecorationRight} />
      
      <View style={styles.content}>
        <Animated.View 
          style={[
            styles.iconContainer,
            { transform: [{ translateY: bounceAnim }] }
          ]}
        >
          <Text style={styles.icon}>🤝</Text>
          <Text style={styles.iconSecondary}>⚕️</Text>
          <Text style={styles.icon}>🔬</Text>
          <Text style={styles.iconSecondary}>📱</Text>
        </Animated.View>
        
        <Animated.View 
          style={[
            styles.messageContainer,
            { transform: [{ scale: pulseAnim }] }
          ]}
        >
          <Text style={styles.messageTitle}>
            <Text style={styles.warningIcon}>⚠️ </Text>
            Ferramenta em desenvolvimento...
          </Text>
          <Text style={styles.message}>
            Uma nova proposta para dar maior agilidade e eficiência no{' '}
            <Text style={styles.appName}>AVAQualis</Text> 2025
          </Text>
          <Text style={styles.partnershipText}>
            <Text style={styles.partnershipHighlight}>🚀 UGITS</Text>
            {' + '}
            <Text style={styles.partnershipHighlight}>📋 UGQ</Text>
            {' = Inovação em Avaliação ✨'}
          </Text>
        </Animated.View>
      </View>
      
      <TouchableOpacity 
        style={styles.closeButton} 
        onPress={handleClose}
        activeOpacity={0.8}
      >
        <Text style={styles.closeIcon}>✕</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  banner: {
    backgroundColor: AVAQualisTheme.colors.primary,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
    ...AVAQualisTheme.shadows.medium,
  },
  topLine: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: AVAQualisTheme.colors.secondary,
    zIndex: 3,
  },
  backgroundDecoration: {
    position: 'absolute',
    top: -20,
    left: -20,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    zIndex: 1,
  },
  backgroundDecorationRight: {
    position: 'absolute',
    bottom: -15,
    right: -15,
    width: 45,
    height: 45,
    borderRadius: 22.5,
    backgroundColor: `${AVAQualisTheme.colors.secondary}15`,
    zIndex: 1,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 2,
  },
  iconContainer: {
    flexDirection: 'row',
    marginRight: 10,
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 8,
  },
  icon: {
    fontSize: 16,
    marginHorizontal: 1,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  iconSecondary: {
    fontSize: 14,
    marginHorizontal: 1,
    opacity: 0.9,
    textShadowColor: 'rgba(0, 0, 0, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  messageContainer: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: AVAQualisTheme.colors.secondary,
  },
  messageTitle: {
    color: AVAQualisTheme.colors.onPrimary,
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 2,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  warningIcon: {
    fontSize: 12,
  },
  message: {
    color: AVAQualisTheme.colors.onPrimary,
    fontSize: 10,
    lineHeight: 14,
    marginBottom: 2,
    opacity: 0.95,
  },
  appName: {
    fontWeight: 'bold',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 3,
    borderRadius: 3,
    textDecorationLine: 'underline',
  },
  partnershipText: {
    color: AVAQualisTheme.colors.onPrimary,
    fontSize: 9,
    opacity: 0.9,
    fontStyle: 'italic',
    fontWeight: '500',
  },
  partnershipHighlight: {
    fontWeight: 'bold',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 4,
    marginHorizontal: 1,
  },
  closeButton: {
    padding: 8,
    marginLeft: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 15,
    minWidth: 30,
    minHeight: 30,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    ...AVAQualisTheme.shadows.small,
  },
  closeIcon: {
    color: AVAQualisTheme.colors.onPrimary,
    fontSize: 16,
    fontWeight: 'bold',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
});